import { Box } from '@chakra-ui/react';
import { MOVEABLE_MULTISELECT_CLASS, WIDGETBASE_CLASS } from 'constants/bounding-box';
import { useEventListener } from 'hooks/useEventListener';
import { PAGE_CONTAINER_CLASSNAME } from 'modules/Editor/Editor.config';
import { RootState, setActiveWidget, store, useAppDispatch } from 'modules/Editor/store';
import { selectActiveWidgetIds, selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { MouseEvent as ReactMouseEvent, useCallback, useRef } from 'react';

import { CONTEXT_MENU_CONTAINER_ID } from './ContextMenu.config';
import { CheckContextMenuItemFnArgs, OnContextMenuItemClickFn } from './ContextMenu.types';
import { CANVAS_ACTIONS, WIDGET_ACTIONS } from './ContextMenuActions';
import { ContextMenuList } from './ContextMenuList';
import { useContextMenu } from './ContextMenuProvider';

export const ContextMenu = () => {
  const { items, setItems, position, isOpen, openMenu, hideMenu } = useContextMenu();
  const contextMenuRef = useRef<HTMLDivElement | null>(null);

  // Data to be passed into the ContextMenuItem checkDisabled/Hidden fns
  const contextMenuData = useRef<CheckContextMenuItemFnArgs | null>(null);
  const dispatch = useAppDispatch();

  const onContextMenu = useCallback(
    (event: Event) => {
      const e = event as MouseEvent;
      const storeState = store.getState() as RootState;
      const activeWidgets = selectActiveWidgets(storeState);

      const isMultiSelect = activeWidgets.length > 1;

      const target = e.target as HTMLElement;

      // If right-clicked outside of canvas (i.e. gray area, sidebar, toolbar, etc.)
      // Should have default context menu
      if (!target || target.closest(`.${PAGE_CONTAINER_CLASSNAME}, .${MOVEABLE_MULTISELECT_CLASS}`) === null) {
        hideMenu();
        return;
      }

      // Target is a widget
      const widgetContainer = target.closest(`.${WIDGETBASE_CLASS}`);

      // For multiselect need to check .moveable-area from react-moveable lib
      const moveableAreaContainer = target.closest(`.${MOVEABLE_MULTISELECT_CLASS}`);

      // Click on multiselect react-moveable bounding box
      if (moveableAreaContainer !== null && isMultiSelect) {
        const activeWidgetIds = selectActiveWidgetIds(storeState);

        contextMenuData.current = {
          activeWidgetIds,
          isMultiSelect: true,
          isGroupWidget: false,
        };

        setItems(WIDGET_ACTIONS);
      } else if (widgetContainer !== null) {
        // Clicked on widget
        const widgetId = widgetContainer.id;
        dispatch(setActiveWidget(widgetId));

        // Need to get the latest active widget ids from store in case
        // A group or responsive widget was selected
        const newStoreState = store.getState() as RootState;
        const newActiveWidgetIds = selectActiveWidgetIds(newStoreState);
        const newActiveWidgets = selectActiveWidgets(newStoreState);

        contextMenuData.current = {
          activeWidgetIds: newActiveWidgetIds,
          isMultiSelect: false,
          isGroupWidget: !!newActiveWidgets?.[0].groupId,
        };

        setItems(WIDGET_ACTIONS);
      } else {
        // Clicked on canvas
        contextMenuData.current = null;
        dispatch(setActiveWidget([]));

        setItems(CANVAS_ACTIONS);
      }

      e.preventDefault();
      openMenu({ x: e.pageX, y: e.pageY });
    },
    [setItems, openMenu, hideMenu, dispatch],
  );

  const handleClickOutside = useCallback(
    (e: Event) => {
      // Check the first child in case it was translated due to overflow
      if (!contextMenuRef.current?.firstChild?.contains(e.target as Node)) {
        hideMenu();
        return;
      }
    },
    [hideMenu],
  );

  // Close menu if click outside
  // Note: Can't use useOutsideClick because menu won't close correctly with drag-select
  useEventListener('mousedown', handleClickOutside);

  // Listen to contextmenu event - triggered by right click
  useEventListener('contextmenu', onContextMenu);

  const itemOnClickEvent = (
    e: ReactMouseEvent<HTMLButtonElement>,
    callback: (args: OnContextMenuItemClickFn) => void,
  ) => {
    e.stopPropagation();

    callback({
      e,
      globalDispatch: dispatch,
      ...contextMenuData.current,
    });
    hideMenu();
  };

  return (
    <Box id={CONTEXT_MENU_CONTAINER_ID} position='relative'>
      {isOpen && (
        <Box
          position='absolute'
          ref={contextMenuRef}
          zIndex='contextMenu'
          top={`${position!.y}px`}
          left={`${position!.x}px`}
        >
          <ContextMenuList
            contextMenuItems={items}
            onClickWrapper={itemOnClickEvent}
            contextMenuData={contextMenuData.current || {}}
            position={position!}
          />
        </Box>
      )}
    </Box>
  );
};
